import { Language } from '../types';

export interface UIStrings {
  navHome: string;
  navDiary: string;
  navAsk: string;
  navDecisions: string;
  navAnalysis: string;
  navProfile: string;
  navSettings: string;
  mapYourPath: string;
  noDecisions: string;
  newDilemma: string;
  decisionPlaceholder: string;
  mapDecisionAction: string;
  pathsMapped: string;
  visualMoment: string;
  diagnosticsSub: string;
  vitalBalance: string;
  trajectory: string;
  personality: string;
  warmth: string;
  directness: string;
  depth: string;
  tuning: string;
  testVoice: string;
  calibrating: string;
  voicePreview: string;
  selectArchetype: string;
  personalityNotice: string;
  mentor: string;
  mentorDesc: string;
  companion: string;
  companionDesc: string;
  stoic: string;
  stoicDesc: string;
  philosopher: string;
  philosopherDesc: string;
  interfaceLang: string;
  resetSystem: string;
  syncing: string;
  loadingProfile: string;
  loadingSync: string;
  avgAlignment: string;
  alignmentScore: string;
  intentionality: string;
  performanceHigh: string;
  performanceMid: string;
  performanceLow: string;
  dialogue: string;
  writeFreely: string;
  listening: string;
  saveThought: string;
  syncComplete: string;
  loadingMoments: string;
  voiceInstruction: string;
  categories: {
    Health: string;
    Focus: string;
    Connection: string;
    Spirit: string;
    Finance: string;
  };
  chatWelcome: {
    greeting: string;
    greetingSubtitle: string;
    description: string;
    startHere: string;
  };
  chatCard: {
    support: string;
    celebration: string;
    exploration: string;
    reflection: string;
    coaching: string;
    insight: string;
    track: string;
    decide: string;
    tracked: string;
    created: string;
    basedOn: string;
    contextUsed: string;
    references: string;
    values: string;
    goals: string;
    patterns: string;
    usingContext: string;
    rememberThis: string;
    saved: string;
    remember: string;
    goDeeper: string;
    continueWith: string;
    balanced: string;
    modeSupport: string;
    modeCelebration: string;
    modeExploration: string;
    lifeIntelligence: string;
    source: string;
    sources: string;
    memoryWhisper: string;
    moments: string;
    people: string;
    places: string;
    decisions: string;
    usedMemories: string;
    iSee: string;
    iNotice: string;
    microAction: string;
    sintonia: string;
    sintoniaHigh: string;
    sintoniaMedium: string;
    sintoniaLow: string;
    basedOnYourHistory: string;
    fromYourPatterns: string;
    connectedTo: string;
    saveValue: string;
    saveGoal: string;
  };
  voice: {
    title: string;
    tapToRecord: string;
    recording: string;
    transcribing: string;
    saveNote: string;
    cancel: string;
    micPermission: string;
    noSpeech: string;
    error: string;
  };
  decisions: {
    subtitle: string;
    analyzeChoice: string;
    analyzing: string;
    discussInChat: string;
    resolve: string;
    resolved: string;
    pending: string;
    options: string;
    pros: string;
    cons: string;
    bestOption: string;
    reasoning: string;
    nextStep: string;
    alignment: string;
  };
  home: {
    goodMorning: string;
    goodAfternoon: string;
    goodEvening: string;
    todayIs: string;
    recentMoments: string;
    noMoments: string;
    startTracking: string;
    addMoment: string;
    placeholder: string;
    saving: string;
    save: string;
    readyToListen: string;
    noMicFound: string;
    noTextDetected: string;
    writeOrHoldMic: string;
    transcribing: string;
    oneMoment: string;
    recordingInProgress: string;
    viewAll: string;
    startCapturing: string;
    iKnowYouAt: string;
    micPermissionError: string;
    recordingError: string;
    recordingTooShort: string;
    transcriptionError: string;
    greeting: string;
    recent: string;
    defaultUser: string;
    connection: string;
    connections: string;
    timeNow: string;
    timeMinutesAgo: string;
    timeHoursAgo: string;
    timeYesterday: string;
    timeDaysAgo: string;
  };
  journal: {
    title: string;
    subtitle: string;
    noEntries: string;
    startJourney: string;
    searchPlaceholder: string;
    allCategories: string;
    viewDetails: string;
    aiInsight: string;
    entries: string;
    vitalImpact: string;
    connections: string;
    delete: string;
    deleteConfirm: string;
    noMemoriesYet: string;
    noResults: string;
    empty: string;
    people: string;
    places: string;
  };
  askSubtitle: string;
  askLabels: {
    moments: string;
    decisions: string;
    patterns: string;
  };
  askSuggestions: {
    goalProgress: string;
    analyzeValues: string;
    difficultDecision: string;
    whatCanYouDo: string;
    analyzeDay: string;
    helpDecide: string;
    whatWouldHelp: string;
    tellMeMore: string;
    whatBlocksYou: string;
    howToCelebrate: string;
    whatWorked: string;
    nextSteps: string;
    deeperOnTopic: string;
    howDoesItFeel: string;
    whatToChange: string;
    firstTime?: {
      tellMeAboutToday: string;
      whatAreYouThinking: string;
      aDecisionToMake: string;
    };
  };
  askPage: {
    momentSaved: string;
    saveError: string;
    saved: string;
    writeOrAskAdvice: string;
    decisionCreated: string;
    valueSaved: string;
    goalSaved: string;
  };
  decisionsPage: {
    subtitle: string;
    strategicAnalysis: string;
    choicePlaceholder: string;
    analyzeDilemma: string;
    aiAdvice: string;
    suggested: string;
    nextStep: string;
    discussNextStep: string;
    compareOptions: string;
    discussInChat: string;
    decided: string;
    reopen: string;
    delete: string;
    waitingForChoice: string;
    talkInChat: string;
    analyzed: string;
    inAnalysis: string;
  };
  insightsPage: {
    knowYouAt: string;
    knowDeep: string;
    niceConnection: string;
    learningAboutYou: string;
    continueLearning: string;
    beginningJourney: string;
    moments: string;
    patterns: string;
    decisions: string;
    people: string;
    whatIKnow: string;
    yourValues: string;
    yourGoals: string;
    addYourValues: string;
    addYourGoals: string;
    newValue: string;
    newGoal: string;
    toKnowYouBetter: string;
    logout: string;
    logoutConfirm: string;
    logoutDescription: string;
  };
  insights: {
    memory: string;
    flow: string;
    personality: string;
    settings: string;
    yourValues: string;
    yourGoals: string;
    addValue: string;
    addGoal: string;
    save: string;
    cancel: string;
    edit: string;
    peopleInYourLife: string;
    patternsDetected: string;
    refreshPatterns: string;
    userName: string;
    dangerZone: string;
    deleteAllData: string;
    deleteWarning: string;
    confirmDelete: string;
    tabMemory: string;
    tabTone: string;
    tabConfig: string;
    lifeAreas: string;
    yourEmotions: string;
    patternsIdentified: string;
    importantPeople: string;
    timesObserved: string;
    timesMentioned: string;
    namePlaceholder: string;
    coreAlignmentExcellent: string;
    coreAlignmentEvolving: string;
    coreAlignmentMessage: string;
    addPerson: string;
    addPersonPlaceholder: string;
    relationshipPlaceholder: string;
    restoreHiddenPeople: string;
    hiddenPeopleCount: string;
    manuallyAdded: string;
    yourTrends: string;
    fiveDimensions: string;
    emotionFlowTitle: string;
    emotionFlowSubtitle: string;
    emotionFlowDaysTracked: string;
    lastDays: string;
    rhythmTitle: string;
    rhythmSubtitle: string;
    rhythmBestMoment: string;
    levelLow: string;
    levelMedium: string;
    levelHigh: string;
    goalAlignmentTitle: string;
    goalAlignmentSubtitle: string;
    alignment: string;
    addGoals: string;
    showMoreGoals: string;
    showLessGoals: string;
    selectGoals: string;
    selectedCount: string;
    noData: string;
    trendsIntroTitle: string;
    trendsIntroDescription: string;
    basedOn: string;
    momentsTracked: string;
    connectedTo: string;
    patternInsight: string;
    noPatterns: string;
    patternTypeEmotional: string;
    patternTypeBehavioral: string;
    patternTypeTemporal: string;
    patternTypeRelational: string;
    patternTypeDecision: string;
  };
  journalLinks: {
    similar: string;
    cause: string;
    effect: string;
    contradiction: string;
    progression: string;
    relatedDecision: string;
  };
  common: {
    loading: string;
    error: string;
    retry: string;
    close: string;
    confirm: string;
    back: string;
    next: string;
    finish: string;
    skip: string;
    or: string;
    and: string;
  };
  errors: {
    generic: string;
    network: string;
    aiUnavailable: string;
    saveFailed: string;
    loadFailed: string;
  };
  limits: {
    title: string;
    subtitle: string;
    momentsLimit: string;
    patternsLimit: string;
    decisionsLimit: string;
    chatMessagesLimit: string;
    currentUsage: string;
    monthlyLimit: string;
    resetsOn: string;
    upgradeTitle: string;
    upgradeDescription: string;
    upgradeButton: string;
    maybeLater: string;
    freeFeatures: string;
    premiumFeatures: string;
  };
}

export const UI_STRINGS: Record<Language, UIStrings> = {
  Italian: {
    navHome: "Pulse",
    navDiary: "Storia",
    navAsk: "Intelletto",
    navDecisions: "Direzione",
    navAnalysis: "Sintesi",
    navProfile: "Profilo",
    navSettings: "Sistema",
    mapYourPath: "Le tue traiettorie",
    noDecisions: "Pace totale.",
    newDilemma: "Esplora dubbio",
    decisionPlaceholder: "Cosa ti turba?",
    mapDecisionAction: "Analizza Percorso",
    pathsMapped: "scelte attive",
    visualMoment: "Istante Visivo",
    diagnosticsSub: "Sintonia attuale",
    vitalBalance: "Orizzonte Vitale",
    trajectory: "Trend",
    personality: "Tono AI",
    warmth: "Empatia",
    directness: "Franchezza",
    depth: "Profondità",
    tuning: "Calibrazione",
    testVoice: "Prova Tono",
    calibrating: "Sintonizzo...",
    voicePreview: "Anteprima Tono",
    selectArchetype: "Seleziona Frequenza",
    personalityNotice: "Questi parametri definiscono come l'AI percepisce e risponde alla tua realtà.",
    mentor: "Mentore",
    mentorDesc: "Focalizzato sull'evoluzione.",
    companion: "Socio",
    companionDesc: "Presente e supportivo.",
    stoic: "Logico",
    stoicDesc: "Analitico e asciutto.",
    philosopher: "Essenza",
    philosopherDesc: "Profondo e poetico.",
    interfaceLang: "Lingua",
    resetSystem: "Ripristina Egovex",
    syncing: "In connessione...",
    loadingProfile: "Caricamento profilo...",
    loadingSync: "Sincronizzazione dati in corso",
    avgAlignment: "Flusso",
    alignmentScore: "Allineamento Core",
    intentionality: "Intenzionalità",
    performanceHigh: "Massima",
    performanceMid: "In crescita",
    performanceLow: "In ascolto",
    dialogue: "Dialogo",
    writeFreely: "Rifletti...",
    listening: "Ti sento...",
    saveThought: "Integra",
    syncComplete: "Link Attivo",
    loadingMoments: "Caricamento momenti...",
    voiceInstruction: "Sei un'essenza di saggezza. Ascolta e rispondi con empatia viscerale ma concisa.",
    categories: {
      Health: "Corpo",
      Focus: "Mente",
      Connection: "Cuore",
      Spirit: "Anima",
      Finance: "Materia"
    },
    chatWelcome: {
      greeting: "questo è il tuo spazio.",
      greetingSubtitle: "Ciao,",
      description: "Scrivi quello che hai in mente. Una riflessione, un dubbio, una decisione da prendere. Sono qui per ascoltare e ragionare insieme a te.",
      startHere: "Inizia da qui"
    },
    chatCard: {
      support: "Supporto",
      celebration: "Celebrazione",
      exploration: "Esplorazione",
      reflection: "Riflessione",
      coaching: "Coaching",
      track: "Traccia",
      decide: "Analizza",
      tracked: "Tracciato",
      created: "Creato",
      basedOn: "Basato su",
      contextUsed: "Contesto usato",
      references: "Riferimenti",
      values: "Valori",
      goals: "Obiettivi",
      patterns: "Pattern",
      usingContext: "Sto usando",
      rememberThis: "Ricorda questo",
      saved: "Salvato",
      remember: "Ricorda",
      goDeeper: "Approfondisci",
      continueWith: "Esploriamo insieme...",
      balanced: "Bilanciato",
      modeSupport: "Supporto",
      modeCelebration: "Celebrazione",
      modeExploration: "Esplorazione",
      lifeIntelligence: "Life Intelligence",
      source: "fonte",
      sources: "fonti",
      memoryWhisper: "ricordi",
      moments: "momenti",
      people: "persone",
      places: "luoghi",
      decisions: "decisioni",
      usedMemories: "ricordi usati",
      insight: "Osservazione",
      iSee: "Vedo che",
      iNotice: "Noto che",
      microAction: "Prova questo",
      sintonia: "Sintonia",
      sintoniaHigh: "In armonia",
      sintoniaMedium: "Da calibrare",
      sintoniaLow: "Attenzione",
      basedOnYourHistory: "Dalla tua storia",
      fromYourPatterns: "Dai tuoi pattern",
      connectedTo: "Collegato a",
      saveValue: "Salva valore",
      saveGoal: "Salva obiettivo"
    },
    voice: {
      title: "Nota Vocale",
      tapToRecord: "Tocca il microfono per registrare",
      recording: "Registrazione in corso...",
      transcribing: "Trascrivo...",
      saveNote: "Salva Nota",
      cancel: "Annulla",
      micPermission: "Permesso microfono richiesto",
      noSpeech: "Nessun audio rilevato",
      error: "Errore durante la registrazione"
    },
    decisions: {
      subtitle: "Risolvi i tuoi dubbi con i dati",
      analyzeChoice: "Analizza Scelta",
      analyzing: "Analizzo...",
      discussInChat: "Discuti in Chat",
      resolve: "Risolvi",
      resolved: "Risolta",
      pending: "In attesa",
      options: "Opzioni",
      pros: "Pro",
      cons: "Contro",
      bestOption: "Opzione Consigliata",
      reasoning: "Motivazione",
      nextStep: "Prossimo Passo",
      alignment: "Allineamento"
    },
    home: {
      goodMorning: "Buongiorno",
      goodAfternoon: "Buon pomeriggio",
      goodEvening: "Buonasera",
      todayIs: "Oggi è",
      recentMoments: "Momenti recenti",
      noMoments: "Nessun momento ancora",
      startTracking: "Inizia a tracciare i tuoi momenti",
      addMoment: "Aggiungi momento",
      placeholder: "Cosa sta succedendo?",
      saving: "Salvo...",
      save: "Salva",
      readyToListen: "Pronto per ascoltarti.",
      noMicFound: "Nessun microfono trovato",
      noTextDetected: "Nessun testo rilevato",
      writeOrHoldMic: "Scrivi o tieni premuto il microfono",
      transcribing: "Sto trascrivendo...",
      oneMoment: "Un momento",
      recordingInProgress: "Registrazione in corso",
      viewAll: "Vedi tutto",
      startCapturing: "Inizia a catturare i tuoi momenti",
      iKnowYouAt: "Ti conosco al",
      micPermissionError: "Permetti l'accesso al microfono nelle impostazioni",
      recordingError: "Errore nell'avvio della registrazione",
      recordingTooShort: "Registrazione troppo breve",
      transcriptionError: "Errore nella trascrizione. Riprova.",
      greeting: "Ciao",
      recent: "Recenti",
      defaultUser: "Utente",
      connection: "collegamento",
      connections: "collegamenti",
      timeNow: "Ora",
      timeMinutesAgo: "m fa",
      timeHoursAgo: "h fa",
      timeYesterday: "Ieri",
      timeDaysAgo: "g fa"
    },
    journal: {
      title: "La tua storia",
      subtitle: "Tutti i tuoi momenti",
      noEntries: "Nessuna voce nel diario",
      startJourney: "Inizia il tuo percorso",
      searchPlaceholder: "Cerca nei tuoi momenti...",
      allCategories: "Tutte le categorie",
      viewDetails: "Vedi dettagli",
      aiInsight: "Insight AI",
      entries: "Momenti",
      vitalImpact: "Impatto Vitale",
      connections: "Collegamenti",
      delete: "Elimina",
      deleteConfirm: "Eliminare questo momento?",
      noMemoriesYet: "Nessun ricordo ancora",
      noResults: "Nessun risultato trovato",
      empty: "Nessun momento ancora",
      people: "Persone",
      places: "Luoghi"
    },
    askSubtitle: "Dialogo esistenziale",
    askLabels: {
      moments: "momenti",
      decisions: "decisioni",
      patterns: "pattern"
    },
    askSuggestions: {
      goalProgress: "Il tuo percorso verso \"{goal}\"",
      analyzeValues: "Cosa rivelano i tuoi valori",
      difficultDecision: "Esplorare questa decisione insieme",
      whatCanYouDo: "Come posso supportarti oggi",
      analyzeDay: "Il significato della tua giornata",
      helpDecide: "Chiarire le tue priorità",
      whatWouldHelp: "Cosa potrebbe sbloccarti ora",
      tellMeMore: "Esploriamo insieme questo tema",
      whatBlocksYou: "Le resistenze nascoste",
      howToCelebrate: "L'importanza di questo traguardo",
      whatWorked: "Cosa ti ha portato qui",
      nextSteps: "Il passo che conta davvero",
      deeperOnTopic: "Il cuore di questa questione",
      howDoesItFeel: "Cosa ti sta comunicando il corpo",
      whatToChange: "Ciò che vuoi trasformare",
      firstTime: {
        tellMeAboutToday: "Raccontami qualcosa che ti è successo oggi",
        whatAreYouThinking: "Condividi cosa stai pensando adesso",
        aDecisionToMake: "Ho una decisione da prendere"
      }
    },
    askPage: {
      momentSaved: "Momento salvato!",
      saveError: "Errore nel salvataggio",
      saved: "Salvato",
      writeOrAskAdvice: "Scrivi o chiedi un consiglio...",
      decisionCreated: "Decisione creata!",
      valueSaved: "Valore salvato!",
      goalSaved: "Obiettivo salvato!"
    },
    decisionsPage: {
      subtitle: "Risolvi i tuoi dubbi con i dati",
      strategicAnalysis: "Analisi Strategica",
      choicePlaceholder: "Quale scelta devi fare?",
      analyzeDilemma: "Analizza Dilemma",
      aiAdvice: "Consiglio AI",
      suggested: "Suggerito",
      nextStep: "Prossimo Step",
      discussNextStep: "Discuti prossimo step",
      compareOptions: "Confronto Opzioni",
      discussInChat: "Discuti in Chat",
      decided: "Deciso",
      reopen: "Riapri",
      delete: "Elimina",
      waitingForChoice: "In attesa di un dubbio da trasformare in scelta.",
      talkInChat: "Parlane in Chat",
      analyzed: "Analizzata",
      inAnalysis: "In Analisi"
    },
    insightsPage: {
      knowYouAt: "Ti conosco al",
      knowDeep: "Ti conosco profondamente!",
      niceConnection: "Abbiamo costruito una bella connessione.",
      learningAboutYou: "Sto imparando tanto su di te.",
      continueLearning: "Continuiamo a conoscerci!",
      beginningJourney: "Siamo appena all'inizio del viaggio.",
      moments: "Momenti",
      patterns: "Pattern",
      decisions: "Decisioni",
      people: "Persone",
      whatIKnow: "Cosa so di te",
      yourValues: "I tuoi valori",
      yourGoals: "I tuoi obiettivi",
      addYourValues: "+ Aggiungi i tuoi valori",
      addYourGoals: "+ Aggiungi i tuoi obiettivi",
      newValue: "Nuovo valore...",
      newGoal: "Nuovo obiettivo...",
      toKnowYouBetter: "Per conoscerti meglio",
      logout: "Esci",
      logoutConfirm: "Vuoi uscire dall'app?",
      logoutDescription: "Verrai disconnesso dall'app. Potrai accedere di nuovo quando vorrai."
    },
    insights: {
      memory: "Memoria",
      flow: "Flusso",
      personality: "Personalità",
      settings: "Impostazioni",
      yourValues: "I tuoi valori",
      yourGoals: "I tuoi obiettivi",
      addValue: "Aggiungi valore",
      addGoal: "Aggiungi obiettivo",
      save: "Salva",
      cancel: "Annulla",
      edit: "Modifica",
      peopleInYourLife: "Persone nella tua vita",
      patternsDetected: "Pattern rilevati",
      refreshPatterns: "Aggiorna pattern",
      userName: "Nome Utente",
      dangerZone: "Zona pericolosa",
      deleteAllData: "Elimina tutti i dati",
      deleteWarning: "Sei sicuro? Tutti i dati verranno cancellati.",
      confirmDelete: "Conferma eliminazione",
      tabMemory: "Memoria",
      tabTone: "Tono",
      tabConfig: "Config",
      lifeAreas: "Aree di vita",
      yourEmotions: "Le tue emozioni",
      patternsIdentified: "Pattern identificati",
      importantPeople: "Persone importanti",
      timesObserved: "volte osservato",
      timesMentioned: "menzionato",
      namePlaceholder: "Nome...",
      coreAlignmentExcellent: "ottimo",
      coreAlignmentEvolving: "in evoluzione",
      coreAlignmentMessage: "Il tuo allineamento core è",
      addPerson: "Aggiungi persona",
      addPersonPlaceholder: "Nome persona...",
      relationshipPlaceholder: "Relazione (es. amico, collega)...",
      restoreHiddenPeople: "Ripristina persone nascoste",
      hiddenPeopleCount: "persone nascoste",
      manuallyAdded: "aggiunta manualmente",
      yourTrends: "I Tuoi Trend",
      fiveDimensions: "Le Tue 5 Dimensioni",
      emotionFlowTitle: "Flusso Emotivo",
      emotionFlowSubtitle: "Come ti sei sentito",
      emotionFlowDaysTracked: "giorni tracciati",
      lastDays: "Ultimi {n} giorni",
      rhythmTitle: "Il Tuo Ritmo",
      rhythmSubtitle: "Quando stai meglio",
      rhythmBestMoment: "Momento ideale",
      levelLow: "Basso",
      levelMedium: "Medio",
      levelHigh: "Alto",
      goalAlignmentTitle: "Allineamento Obiettivi",
      goalAlignmentSubtitle: "Quanto stai seguendo i tuoi goal",
      alignment: "Allineamento",
      addGoals: "Aggiungi i tuoi obiettivi",
      showMoreGoals: "Mostra altri",
      showLessGoals: "Mostra meno",
      selectGoals: "Seleziona obiettivi",
      selectedCount: "selezionati",
      last7Days: "7 giorni",
      noData: "Nessun dato",
      trendsIntroTitle: "Scopri i Tuoi Pattern di Vita",
      trendsIntroDescription: "L'AI analizza continuamente i tuoi momenti per identificare tendenze significative. Questi insight ti aiutano a capire cosa ti fa stare bene, quando sei più produttivo e come evolvono le tue emozioni nel tempo.",
      basedOn: "Basato su",
      momentsTracked: "momenti tracciati",
      connectedTo: "Collegato a",
      patternInsight: "Insight",
      noPatterns: "Continua a condividere momenti per scoprire i tuoi pattern",
      patternTypeEmotional: "Emotivo",
      patternTypeBehavioral: "Comportamentale",
      patternTypeTemporal: "Temporale",
      patternTypeRelational: "Relazionale",
      patternTypeDecision: "Decisionale"
    },
    journalLinks: {
      similar: "Simile",
      cause: "Causa",
      effect: "Effetto",
      contradiction: "Contraddizione",
      progression: "Progressione",
      relatedDecision: "Decisione"
    },
    common: {
      loading: "Caricamento...",
      error: "Errore",
      retry: "Riprova",
      close: "Chiudi",
      confirm: "Conferma",
      back: "Indietro",
      next: "Continua",
      finish: "Fine",
      skip: "Salta",
      or: "o",
      and: "e"
    },
    errors: {
      generic: "Qualcosa è andato storto",
      network: "Errore di connessione",
      aiUnavailable: "AI temporaneamente non disponibile",
      saveFailed: "Salvataggio fallito",
      loadFailed: "Caricamento fallito"
    },
    limits: {
      title: "Hai raggiunto il limite mensile",
      subtitle: "Stai usando Egovex in modo fantastico. Per continuare a crescere, considera l'upgrade.",
      momentsLimit: "momenti",
      patternsLimit: "pattern",
      decisionsLimit: "decisioni",
      chatMessagesLimit: "messaggi",
      currentUsage: "Utilizzati",
      monthlyLimit: "Limite mensile",
      resetsOn: "Si azzera il",
      upgradeTitle: "Sblocca il tuo potenziale",
      upgradeDescription: "Con Premium hai accesso illimitato a tutti gli strumenti per la tua crescita personale.",
      upgradeButton: "Passa a Premium",
      maybeLater: "Non ora, grazie",
      freeFeatures: "Piano gratuito",
      premiumFeatures: "Piano Premium"
    }
  },
  English: {
    navHome: "Pulse",
    navDiary: "History",
    navAsk: "Intellect",
    navDecisions: "Path",
    navAnalysis: "Synthesis",
    navProfile: "Profile",
    navSettings: "System",
    mapYourPath: "Your trajectories",
    noDecisions: "Total peace.",
    newDilemma: "Explore doubt",
    decisionPlaceholder: "What's on your mind?",
    mapDecisionAction: "Map Path",
    pathsMapped: "active choices",
    visualMoment: "Visual Instance",
    diagnosticsSub: "Current resonance",
    vitalBalance: "Vital Balance",
    trajectory: "Trend",
    personality: "AI Tone",
    warmth: "Empathy",
    directness: "Clarity",
    depth: "Depth",
    tuning: "Calibration",
    testVoice: "Test Tone",
    calibrating: "Tuning...",
    voicePreview: "Tone Preview",
    selectArchetype: "Select Frequency",
    personalityNotice: "These parameters define how the AI perceives and responds to your reality.",
    mentor: "Mentor",
    mentorDesc: "Focused on evolution.",
    companion: "Partner",
    companionDesc: "Present and supportive.",
    stoic: "Logic",
    stoicDesc: "Analytical and crisp.",
    philosopher: "Essence",
    philosopherDesc: "Deep and poetic.",
    interfaceLang: "Language",
    resetSystem: "Reset Egovex",
    syncing: "Connecting...",
    loadingProfile: "Loading profile...",
    loadingSync: "Syncing your data",
    avgAlignment: "Flow",
    alignmentScore: "Core Alignment",
    intentionality: "Intentionality",
    performanceHigh: "Peak",
    performanceMid: "Growing",
    performanceLow: "Listening",
    dialogue: "Dialogue",
    writeFreely: "Reflect...",
    listening: "I hear you...",
    saveThought: "Integrate",
    syncComplete: "Link Active",
    loadingMoments: "Loading moments...",
    voiceInstruction: "You are an essence of wisdom. Listen and respond with visceral yet concise empathy.",
    categories: {
      Health: "Body",
      Focus: "Mind",
      Connection: "Heart",
      Spirit: "Soul",
      Finance: "Matter"
    },
    chatWelcome: {
      greeting: "this is your space.",
      greetingSubtitle: "Hello,",
      description: "Write what's on your mind. A reflection, a doubt, a decision to make. I'm here to listen and think with you.",
      startHere: "Start here"
    },
    chatCard: {
      support: "Support",
      celebration: "Celebration",
      exploration: "Exploration",
      reflection: "Reflection",
      coaching: "Coaching",
      track: "Track",
      decide: "Analyze",
      tracked: "Tracked",
      created: "Created",
      basedOn: "Based on",
      contextUsed: "Context used",
      references: "References",
      values: "Values",
      goals: "Goals",
      patterns: "Patterns",
      usingContext: "Using",
      rememberThis: "Remember this",
      saved: "Saved",
      remember: "Remember",
      goDeeper: "Go deeper",
      continueWith: "Let's explore...",
      balanced: "Balanced",
      modeSupport: "Support",
      modeCelebration: "Celebration",
      modeExploration: "Exploration",
      lifeIntelligence: "Life Intelligence",
      source: "source",
      sources: "sources",
      memoryWhisper: "memories",
      moments: "moments",
      people: "people",
      places: "places",
      decisions: "decisions",
      usedMemories: "memories",
      insight: "Insight",
      iSee: "I see that",
      iNotice: "I notice that",
      microAction: "Try this",
      sintonia: "Alignment",
      sintoniaHigh: "In harmony",
      sintoniaMedium: "Needs tuning",
      sintoniaLow: "Attention",
      basedOnYourHistory: "From your history",
      fromYourPatterns: "From your patterns",
      connectedTo: "Connected to",
      saveValue: "Save value",
      saveGoal: "Save goal"
    },
    voice: {
      title: "Voice Note",
      tapToRecord: "Tap the microphone to record",
      recording: "Recording...",
      transcribing: "Transcribing...",
      saveNote: "Save Note",
      cancel: "Cancel",
      micPermission: "Microphone permission required",
      noSpeech: "No speech detected",
      error: "Error during recording"
    },
    decisions: {
      subtitle: "Resolve your doubts with data",
      analyzeChoice: "Analyze Choice",
      analyzing: "Analyzing...",
      discussInChat: "Discuss in Chat",
      resolve: "Resolve",
      resolved: "Resolved",
      pending: "Pending",
      options: "Options",
      pros: "Pros",
      cons: "Cons",
      bestOption: "Recommended Option",
      reasoning: "Reasoning",
      nextStep: "Next Step",
      alignment: "Alignment"
    },
    home: {
      goodMorning: "Good morning",
      goodAfternoon: "Good afternoon",
      goodEvening: "Good evening",
      todayIs: "Today is",
      recentMoments: "Recent moments",
      noMoments: "No moments yet",
      startTracking: "Start tracking your moments",
      addMoment: "Add moment",
      placeholder: "What's happening?",
      saving: "Saving...",
      save: "Save",
      readyToListen: "Ready to listen.",
      noMicFound: "No microphone found",
      noTextDetected: "No text detected",
      writeOrHoldMic: "Write or hold the microphone",
      transcribing: "Transcribing...",
      oneMoment: "One moment",
      recordingInProgress: "Recording in progress",
      viewAll: "View all",
      startCapturing: "Start capturing your moments",
      iKnowYouAt: "I know you at",
      micPermissionError: "Allow microphone access in settings",
      recordingError: "Error starting recording",
      recordingTooShort: "Recording too short",
      transcriptionError: "Transcription error. Please try again.",
      greeting: "Hello",
      recent: "Recent",
      defaultUser: "User",
      connection: "connection",
      connections: "connections",
      timeNow: "Now",
      timeMinutesAgo: "m ago",
      timeHoursAgo: "h ago",
      timeYesterday: "Yesterday",
      timeDaysAgo: "d ago"
    },
    journal: {
      title: "Your story",
      subtitle: "All your moments",
      noEntries: "No journal entries",
      startJourney: "Start your journey",
      searchPlaceholder: "Search your moments...",
      allCategories: "All categories",
      viewDetails: "View details",
      aiInsight: "AI Insight",
      entries: "Moments",
      vitalImpact: "Vital Impact",
      connections: "Connections",
      delete: "Delete",
      deleteConfirm: "Delete this moment?",
      noMemoriesYet: "No memories yet",
      noResults: "No results found",
      empty: "No moments yet",
      people: "People",
      places: "Places"
    },
    askSubtitle: "Existential dialogue",
    askLabels: {
      moments: "moments",
      decisions: "decisions",
      patterns: "patterns"
    },
    askSuggestions: {
      goalProgress: "Your journey toward \"{goal}\"",
      analyzeValues: "What your values reveal",
      difficultDecision: "Exploring this decision together",
      whatCanYouDo: "How I can support you today",
      analyzeDay: "The meaning in your day",
      helpDecide: "Clarifying your priorities",
      whatWouldHelp: "What might unblock you now",
      tellMeMore: "Let's explore this together",
      whatBlocksYou: "The hidden resistances",
      howToCelebrate: "The significance of this milestone",
      whatWorked: "What brought you here",
      nextSteps: "The step that really matters",
      deeperOnTopic: "The heart of this matter",
      howDoesItFeel: "What your body is telling you",
      whatToChange: "What you want to transform",
      firstTime: {
        tellMeAboutToday: "Tell me something that happened today",
        whatAreYouThinking: "Share what you're thinking right now",
        aDecisionToMake: "I have a decision to make"
      }
    },
    askPage: {
      momentSaved: "Moment saved!",
      saveError: "Error saving",
      saved: "Saved",
      writeOrAskAdvice: "Write or ask for advice...",
      decisionCreated: "Decision created!",
      valueSaved: "Value saved!",
      goalSaved: "Goal saved!"
    },
    decisionsPage: {
      subtitle: "Resolve your doubts with data",
      strategicAnalysis: "Strategic Analysis",
      choicePlaceholder: "What choice do you need to make?",
      analyzeDilemma: "Analyze Dilemma",
      aiAdvice: "AI Advice",
      suggested: "Suggested",
      nextStep: "Next Step",
      discussNextStep: "Discuss next step",
      compareOptions: "Compare Options",
      discussInChat: "Discuss in Chat",
      decided: "Decided",
      reopen: "Reopen",
      delete: "Delete",
      waitingForChoice: "Waiting for a doubt to transform into a choice.",
      talkInChat: "Talk in Chat",
      analyzed: "Analyzed",
      inAnalysis: "Analyzing"
    },
    insightsPage: {
      knowYouAt: "I know you at",
      knowDeep: "I know you deeply!",
      niceConnection: "We've built a nice connection.",
      learningAboutYou: "I'm learning a lot about you.",
      continueLearning: "Let's keep getting to know each other!",
      beginningJourney: "We're just beginning the journey.",
      moments: "Moments",
      patterns: "Patterns",
      decisions: "Decisions",
      people: "People",
      whatIKnow: "What I know about you",
      yourValues: "Your values",
      yourGoals: "Your goals",
      addYourValues: "+ Add your values",
      addYourGoals: "+ Add your goals",
      newValue: "New value...",
      newGoal: "New goal...",
      toKnowYouBetter: "To know you better",
      logout: "Logout",
      logoutConfirm: "Do you want to log out?",
      logoutDescription: "You will be logged out from the app. You can log in again whenever you want."
    },
    insights: {
      memory: "Memory",
      flow: "Flow",
      personality: "Personality",
      settings: "Settings",
      yourValues: "Your values",
      yourGoals: "Your goals",
      addValue: "Add value",
      addGoal: "Add goal",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      peopleInYourLife: "People in your life",
      patternsDetected: "Patterns detected",
      refreshPatterns: "Refresh patterns",
      userName: "User Name",
      dangerZone: "Danger zone",
      deleteAllData: "Delete all data",
      deleteWarning: "Are you sure? All data will be deleted.",
      confirmDelete: "Confirm deletion",
      tabMemory: "Memory",
      tabTone: "Tone",
      tabConfig: "Config",
      lifeAreas: "Life areas",
      yourEmotions: "Your emotions",
      patternsIdentified: "Patterns identified",
      importantPeople: "Important people",
      timesObserved: "times observed",
      timesMentioned: "mentioned",
      namePlaceholder: "Name...",
      coreAlignmentExcellent: "excellent",
      coreAlignmentEvolving: "evolving",
      coreAlignmentMessage: "Your core alignment is",
      addPerson: "Add person",
      addPersonPlaceholder: "Person name...",
      relationshipPlaceholder: "Relationship (e.g. friend, colleague)...",
      restoreHiddenPeople: "Restore hidden people",
      hiddenPeopleCount: "hidden people",
      manuallyAdded: "manually added",
      yourTrends: "Your Trends",
      fiveDimensions: "Your 5 Dimensions",
      emotionFlowTitle: "Emotion Flow",
      emotionFlowSubtitle: "How you've been feeling",
      emotionFlowDaysTracked: "days tracked",
      lastDays: "Last {n} days",
      rhythmTitle: "Your Rhythm",
      rhythmSubtitle: "When you feel best",
      rhythmBestMoment: "Best time",
      levelLow: "Low",
      levelMedium: "Medium",
      levelHigh: "High",
      goalAlignmentTitle: "Goal Alignment",
      goalAlignmentSubtitle: "How well you're following your goals",
      alignment: "Alignment",
      addGoals: "Add your goals",
      showMoreGoals: "Show more",
      showLessGoals: "Show less",
      selectGoals: "Select goals",
      selectedCount: "selected",
      noData: "No data",
      trendsIntroTitle: "Discover Your Life Patterns",
      trendsIntroDescription: "AI continuously analyzes your moments to identify meaningful trends. These insights help you understand what makes you feel good, when you're most productive, and how your emotions evolve over time.",
      basedOn: "Based on",
      momentsTracked: "tracked moments",
      connectedTo: "Connected to",
      patternInsight: "Insight",
      noPatterns: "Keep sharing moments to discover your patterns",
      patternTypeEmotional: "Emotional",
      patternTypeBehavioral: "Behavioral",
      patternTypeTemporal: "Temporal",
      patternTypeRelational: "Relational",
      patternTypeDecision: "Decision"
    },
    journalLinks: {
      similar: "Similar",
      cause: "Cause",
      effect: "Effect",
      contradiction: "Contradiction",
      progression: "Progression",
      relatedDecision: "Decision"
    },
    common: {
      loading: "Loading...",
      error: "Error",
      retry: "Retry",
      close: "Close",
      confirm: "Confirm",
      back: "Back",
      next: "Continue",
      finish: "Finish",
      skip: "Skip",
      or: "or",
      and: "and"
    },
    errors: {
      generic: "Something went wrong",
      network: "Connection error",
      aiUnavailable: "AI temporarily unavailable",
      saveFailed: "Save failed",
      loadFailed: "Load failed"
    },
    limits: {
      title: "You've reached your monthly limit",
      subtitle: "You're using Egovex beautifully. To keep growing, consider upgrading.",
      momentsLimit: "moments",
      patternsLimit: "patterns",
      decisionsLimit: "decisions",
      chatMessagesLimit: "messages",
      currentUsage: "Used",
      monthlyLimit: "Monthly limit",
      resetsOn: "Resets on",
      upgradeTitle: "Unlock your potential",
      upgradeDescription: "With Premium, get unlimited access to all tools for your personal growth.",
      upgradeButton: "Upgrade to Premium",
      maybeLater: "Not now, thanks",
      freeFeatures: "Free plan",
      premiumFeatures: "Premium plan"
    }
  },
  Spanish: {
    navHome: "Pulse",
    navDiary: "Historia",
    navAsk: "Intelecto",
    navDecisions: "Camino",
    navAnalysis: "Síntesis",
    navProfile: "Perfil",
    navSettings: "Sistema",
    mapYourPath: "Tus trayectorias",
    noDecisions: "Paz total.",
    newDilemma: "Explorar duda",
    decisionPlaceholder: "¿Qué tienes en mente?",
    mapDecisionAction: "Mapear Camino",
    pathsMapped: "opciones activas",
    visualMoment: "Instante Visual",
    diagnosticsSub: "Resonancia actual",
    vitalBalance: "Equilibrio Vital",
    trajectory: "Tendencia",
    personality: "Tono AI",
    warmth: "Empatía",
    directness: "Claridad",
    depth: "Profundidad",
    tuning: "Calibración",
    testVoice: "Probar Tono",
    calibrating: "Sintonizando...",
    voicePreview: "Vista Previa del Tono",
    selectArchetype: "Seleccionar Frecuencia",
    personalityNotice: "Estos parámetros definen cómo la IA percibe y responde a tu realidad.",
    mentor: "Mentor",
    mentorDesc: "Enfocado en la evolución.",
    companion: "Compañero",
    companionDesc: "Presente y solidario.",
    stoic: "Lógica",
    stoicDesc: "Analítico y conciso.",
    philosopher: "Esencia",
    philosopherDesc: "Profundo y poético.",
    interfaceLang: "Idioma",
    resetSystem: "Restablecer Egovex",
    syncing: "Conectando...",
    loadingProfile: "Cargando perfil...",
    loadingSync: "Sincronizando tus datos",
    avgAlignment: "Flujo",
    alignmentScore: "Alineación Core",
    intentionality: "Intencionalidad",
    performanceHigh: "Pico",
    performanceMid: "Creciendo",
    performanceLow: "Escuchando",
    dialogue: "Diálogo",
    writeFreely: "Reflexiona...",
    listening: "Te escucho...",
    saveThought: "Integrar",
    syncComplete: "Enlace Activo",
    loadingMoments: "Cargando momentos...",
    voiceInstruction: "Eres una esencia de sabiduría. Escucha y responde con una empatía visceral pero concisa.",
    categories: {
      Health: "Cuerpo",
      Focus: "Mente",
      Connection: "Corazón",
      Spirit: "Alma",
      Finance: "Materia"
    },
    chatWelcome: {
      greeting: "este es tu espacio.",
      greetingSubtitle: "Hola,",
      description: "Escribe lo que tienes en mente. Una reflexión, una duda, una decisión por tomar. Estoy aquí para escuchar y razonar contigo.",
      startHere: "Empieza aquí"
    },
    chatCard: {
      support: "Apoyo",
      celebration: "Celebración",
      exploration: "Exploración",
      reflection: "Reflexión",
      coaching: "Coaching",
      track: "Registrar",
      decide: "Analizar",
      tracked: "Registrado",
      created: "Creado",
      basedOn: "Basado en",
      contextUsed: "Contexto usado",
      references: "Referencias",
      values: "Valores",
      goals: "Objetivos",
      patterns: "Patrones",
      usingContext: "Usando",
      rememberThis: "Recordar esto",
      saved: "Guardado",
      remember: "Recordar",
      goDeeper: "Profundizar",
      continueWith: "Exploremos juntos...",
      balanced: "Equilibrado",
      modeSupport: "Apoyo",
      modeCelebration: "Celebración",
      modeExploration: "Exploración",
      lifeIntelligence: "Life Intelligence",
      source: "fuente",
      sources: "fuentes",
      memoryWhisper: "recuerdos",
      moments: "momentos",
      people: "personas",
      places: "lugares",
      decisions: "decisiones",
      usedMemories: "recuerdos",
      insight: "Observación",
      iSee: "Veo que",
      iNotice: "Noto que",
      microAction: "Prueba esto",
      sintonia: "Sintonía",
      sintoniaHigh: "En armonía",
      sintoniaMedium: "Por calibrar",
      sintoniaLow: "Atención",
      basedOnYourHistory: "De tu historia",
      fromYourPatterns: "De tus patrones",
      connectedTo: "Conectado a",
      saveValue: "Guardar valor",
      saveGoal: "Guardar objetivo"
    },
    voice: {
      title: "Nota de Voz",
      tapToRecord: "Toca el micrófono para grabar",
      recording: "Grabando...",
      transcribing: "Transcribiendo...",
      saveNote: "Guardar Nota",
      cancel: "Cancelar",
      micPermission: "Se requiere permiso del micrófono",
      noSpeech: "No se detectó audio",
      error: "Error durante la grabación"
    },
    decisions: {
      subtitle: "Resuelve tus dudas con datos",
      analyzeChoice: "Analizar Elección",
      analyzing: "Analizando...",
      discussInChat: "Discutir en Chat",
      resolve: "Resolver",
      resolved: "Resuelta",
      pending: "Pendiente",
      options: "Opciones",
      pros: "Pros",
      cons: "Contras",
      bestOption: "Opción Recomendada",
      reasoning: "Razonamiento",
      nextStep: "Próximo Paso",
      alignment: "Alineación"
    },
    home: {
      goodMorning: "Buenos días",
      goodAfternoon: "Buenas tardes",
      goodEvening: "Buenas noches",
      todayIs: "Hoy es",
      recentMoments: "Momentos recientes",
      noMoments: "Sin momentos aún",
      startTracking: "Empieza a registrar tus momentos",
      addMoment: "Añadir momento",
      placeholder: "¿Qué está pasando?",
      saving: "Guardando...",
      save: "Guardar",
      readyToListen: "Listo para escucharte.",
      noMicFound: "No se encontró micrófono",
      noTextDetected: "No se detectó texto",
      writeOrHoldMic: "Escribe o mantén pulsado el micrófono",
      transcribing: "Transcribiendo...",
      oneMoment: "Un momento",
      recordingInProgress: "Grabación en curso",
      viewAll: "Ver todo",
      startCapturing: "Empieza a capturar tus momentos",
      iKnowYouAt: "Te conozco al",
      micPermissionError: "Permite el acceso al micrófono en ajustes",
      recordingError: "Error al iniciar grabación",
      recordingTooShort: "Grabación muy corta",
      transcriptionError: "Error en transcripción. Inténtalo de nuevo.",
      greeting: "Hola",
      recent: "Recientes",
      defaultUser: "Usuario",
      connection: "conexión",
      connections: "conexiones",
      timeNow: "Ahora",
      timeMinutesAgo: "m atrás",
      timeHoursAgo: "h atrás",
      timeYesterday: "Ayer",
      timeDaysAgo: "d atrás"
    },
    journal: {
      title: "Tu historia",
      subtitle: "Todos tus momentos",
      noEntries: "Sin entradas en el diario",
      startJourney: "Comienza tu viaje",
      searchPlaceholder: "Buscar en tus momentos...",
      allCategories: "Todas las categorías",
      viewDetails: "Ver detalles",
      aiInsight: "Insight AI",
      entries: "Momentos",
      vitalImpact: "Impacto Vital",
      connections: "Conexiones",
      delete: "Eliminar",
      deleteConfirm: "¿Eliminar este momento?",
      noMemoriesYet: "Sin recuerdos aún",
      noResults: "Sin resultados",
      empty: "Aún no hay momentos",
      people: "Personas",
      places: "Lugares"
    },
    askSubtitle: "Diálogo existencial",
    askLabels: {
      moments: "momentos",
      decisions: "decisiones",
      patterns: "patrones"
    },
    askSuggestions: {
      goalProgress: "Tu camino hacia \"{goal}\"",
      analyzeValues: "Lo que revelan tus valores",
      difficultDecision: "Explorando esta decisión juntos",
      whatCanYouDo: "Cómo puedo apoyarte hoy",
      analyzeDay: "El significado de tu día",
      helpDecide: "Clarificando tus prioridades",
      whatWouldHelp: "Qué podría desbloquearte ahora",
      tellMeMore: "Exploremos esto juntos",
      whatBlocksYou: "Las resistencias ocultas",
      howToCelebrate: "El significado de este logro",
      whatWorked: "Lo que te trajo hasta aquí",
      nextSteps: "El paso que realmente importa",
      deeperOnTopic: "El corazón de este asunto",
      howDoesItFeel: "Lo que tu cuerpo te dice",
      whatToChange: "Lo que quieres transformar",
      firstTime: {
        tellMeAboutToday: "Cuéntame algo que te pasó hoy",
        whatAreYouThinking: "Comparte lo que estás pensando ahora",
        aDecisionToMake: "Tengo una decisión que tomar"
      }
    },
    askPage: {
      momentSaved: "¡Momento guardado!",
      saveError: "Error al guardar",
      saved: "Guardado",
      writeOrAskAdvice: "Escribe o pide consejo...",
      decisionCreated: "¡Decisión creada!",
      valueSaved: "¡Valor guardado!",
      goalSaved: "¡Objetivo guardado!"
    },
    decisionsPage: {
      subtitle: "Resuelve tus dudas con datos",
      strategicAnalysis: "Análisis Estratégico",
      choicePlaceholder: "¿Qué elección necesitas hacer?",
      analyzeDilemma: "Analizar Dilema",
      aiAdvice: "Consejo IA",
      suggested: "Sugerido",
      nextStep: "Próximo Paso",
      discussNextStep: "Discutir próximo paso",
      compareOptions: "Comparar Opciones",
      discussInChat: "Discutir en Chat",
      decided: "Decidido",
      reopen: "Reabrir",
      delete: "Eliminar",
      waitingForChoice: "Esperando una duda para transformar en elección.",
      talkInChat: "Hablar en Chat",
      analyzed: "Analizada",
      inAnalysis: "Analizando"
    },
    insightsPage: {
      knowYouAt: "Te conozco al",
      knowDeep: "¡Te conozco profundamente!",
      niceConnection: "Hemos construido una buena conexión.",
      learningAboutYou: "Estoy aprendiendo mucho sobre ti.",
      continueLearning: "¡Sigamos conociéndonos!",
      beginningJourney: "Estamos apenas al inicio del viaje.",
      moments: "Momentos",
      patterns: "Patrones",
      decisions: "Decisiones",
      people: "Personas",
      whatIKnow: "Lo que sé de ti",
      yourValues: "Tus valores",
      yourGoals: "Tus objetivos",
      addYourValues: "+ Añadir tus valores",
      addYourGoals: "+ Añadir tus objetivos",
      newValue: "Nuevo valor...",
      newGoal: "Nuevo objetivo...",
      toKnowYouBetter: "Para conocerte mejor",
      logout: "Cerrar sesión",
      logoutConfirm: "¿Quieres cerrar sesión?",
      logoutDescription: "Serás desconectado de la app. Puedes iniciar sesión de nuevo cuando quieras."
    },
    insights: {
      memory: "Memoria",
      flow: "Flujo",
      personality: "Personalidad",
      settings: "Configuración",
      yourValues: "Tus valores",
      yourGoals: "Tus objetivos",
      addValue: "Añadir valor",
      addGoal: "Añadir objetivo",
      save: "Guardar",
      cancel: "Cancelar",
      edit: "Editar",
      peopleInYourLife: "Personas en tu vida",
      patternsDetected: "Patrones detectados",
      refreshPatterns: "Actualizar patrones",
      userName: "Nombre de Usuario",
      dangerZone: "Zona de peligro",
      deleteAllData: "Eliminar todos los datos",
      deleteWarning: "Esta acción es irreversible",
      confirmDelete: "Confirmar eliminación",
      tabMemory: "Memoria",
      tabTone: "Tono",
      tabConfig: "Config",
      lifeAreas: "Áreas de vida",
      yourEmotions: "Tus emociones",
      patternsIdentified: "Patrones identificados",
      importantPeople: "Personas importantes",
      timesObserved: "veces observado",
      timesMentioned: "mencionado",
      namePlaceholder: "Nombre...",
      coreAlignmentExcellent: "excelente",
      coreAlignmentEvolving: "en evolución",
      coreAlignmentMessage: "Tu alineación core es",
      addPerson: "Añadir persona",
      addPersonPlaceholder: "Nombre de persona...",
      relationshipPlaceholder: "Relación (ej. amigo, colega)...",
      restoreHiddenPeople: "Restaurar personas ocultas",
      hiddenPeopleCount: "personas ocultas",
      manuallyAdded: "añadida manualmente",
      yourTrends: "Tus Tendencias",
      fiveDimensions: "Tus 5 Dimensiones",
      emotionFlowTitle: "Flujo Emocional",
      emotionFlowSubtitle: "Cómo te has sentido",
      emotionFlowDaysTracked: "días rastreados",
      lastDays: "Últimos {n} días",
      rhythmTitle: "Tu Ritmo",
      rhythmSubtitle: "Cuándo te sientes mejor",
      rhythmBestMoment: "Mejor momento",
      levelLow: "Bajo",
      levelMedium: "Medio",
      levelHigh: "Alto",
      goalAlignmentTitle: "Alineación de Objetivos",
      goalAlignmentSubtitle: "Qué tan bien sigues tus metas",
      alignment: "Alineación",
      addGoals: "Añade tus objetivos",
      showMoreGoals: "Mostrar más",
      showLessGoals: "Mostrar menos",
      selectGoals: "Seleccionar objetivos",
      selectedCount: "seleccionados",
      noData: "Sin datos",
      trendsIntroTitle: "Descubre Tus Patrones de Vida",
      trendsIntroDescription: "La IA analiza continuamente tus momentos para identificar tendencias significativas. Estos insights te ayudan a entender qué te hace sentir bien, cuándo eres más productivo y cómo evolucionan tus emociones con el tiempo.",
      basedOn: "Basado en",
      momentsTracked: "momentos rastreados",
      connectedTo: "Conectado a",
      patternInsight: "Insight",
      noPatterns: "Sigue compartiendo momentos para descubrir tus patrones",
      patternTypeEmotional: "Emocional",
      patternTypeBehavioral: "Conductual",
      patternTypeTemporal: "Temporal",
      patternTypeRelational: "Relacional",
      patternTypeDecision: "Decisión"
    },
    journalLinks: {
      similar: "Similar",
      cause: "Causa",
      effect: "Efecto",
      contradiction: "Contradicción",
      progression: "Progresión",
      relatedDecision: "Decisión"
    },
    common: {
      loading: "Cargando...",
      error: "Error",
      retry: "Reintentar",
      close: "Cerrar",
      confirm: "Confirmar",
      back: "Atrás",
      next: "Continuar",
      finish: "Terminar",
      skip: "Saltar",
      or: "o",
      and: "y"
    },
    errors: {
      generic: "Algo salió mal",
      network: "Error de conexión",
      aiUnavailable: "IA temporalmente no disponible",
      saveFailed: "Error al guardar",
      loadFailed: "Error al cargar"
    },
    limits: {
      title: "Has alcanzado tu límite mensual",
      subtitle: "Estás usando Egovex de manera increíble. Para seguir creciendo, considera la actualización.",
      momentsLimit: "momentos",
      patternsLimit: "patrones",
      decisionsLimit: "decisiones",
      chatMessagesLimit: "mensajes",
      currentUsage: "Usados",
      monthlyLimit: "Límite mensual",
      resetsOn: "Se reinicia el",
      upgradeTitle: "Desbloquea tu potencial",
      upgradeDescription: "Con Premium tienes acceso ilimitado a todas las herramientas para tu crecimiento personal.",
      upgradeButton: "Pasar a Premium",
      maybeLater: "Ahora no, gracias",
      freeFeatures: "Plan gratuito",
      premiumFeatures: "Plan Premium"
    }
  },
  German: {
    navHome: "Puls",
    navDiary: "Geschichte",
    navAsk: "Intellekt",
    navDecisions: "Pfad",
    navAnalysis: "Synthese",
    navProfile: "Profil",
    navSettings: "System",
    mapYourPath: "Deine Trajektorien",
    noDecisions: "Vollständiger Frieden.",
    newDilemma: "Zweifel erkunden",
    decisionPlaceholder: "Was beschäftigt dich?",
    mapDecisionAction: "Pfad kartieren",
    pathsMapped: "aktive Entscheidungen",
    visualMoment: "Visueller Moment",
    diagnosticsSub: "Aktuelle Resonanz",
    vitalBalance: "Vitales Gleichgewicht",
    trajectory: "Trend",
    personality: "KI-Ton",
    warmth: "Empathie",
    directness: "Klarheit",
    depth: "Tiefe",
    tuning: "Kalibrierung",
    testVoice: "Ton testen",
    calibrating: "Stimmen...",
    voicePreview: "Ton-Vorschau",
    selectArchetype: "Frequenz wählen",
    personalityNotice: "Diese Parameter definieren, wie die KI deine Realität wahrnimmt und darauf reagiert.",
    mentor: "Mentor",
    mentorDesc: "Fokussiert auf Entwicklung.",
    companion: "Partner",
    companionDesc: "Präsent und unterstützend.",
    stoic: "Logik",
    stoicDesc: "Analytisch und präzise.",
    philosopher: "Essenz",
    philosopherDesc: "Tief und poetisch.",
    interfaceLang: "Sprache",
    resetSystem: "Egovex zurücksetzen",
    syncing: "Verbinden...",
    loadingProfile: "Profil wird geladen...",
    loadingSync: "Daten werden synchronisiert",
    avgAlignment: "Flow",
    alignmentScore: "Core-Ausrichtung",
    intentionality: "Intentionalität",
    performanceHigh: "Höchstleistung",
    performanceMid: "Wachsend",
    performanceLow: "Zuhörend",
    dialogue: "Dialog",
    writeFreely: "Reflektieren...",
    listening: "Ich höre dir zu...",
    saveThought: "Integrieren",
    syncComplete: "Link aktiv",
    loadingMoments: "Momente werden geladen...",
    voiceInstruction: "Du bist eine Essenz der Weisheit. Höre zu und antworte mit tiefem, aber prägnantem Mitgefühl.",
    categories: {
      Health: "Körper",
      Focus: "Geist",
      Connection: "Herz",
      Spirit: "Seele",
      Finance: "Materie"
    },
    chatWelcome: {
      greeting: "dies ist dein Raum.",
      greetingSubtitle: "Hallo,",
      description: "Schreibe, was dir durch den Kopf geht. Eine Reflexion, ein Zweifel, eine Entscheidung. Ich bin hier, um zuzuhören und mit dir zu denken.",
      startHere: "Beginne hier"
    },
    chatCard: {
      support: "Unterstützung",
      celebration: "Feier",
      exploration: "Erkundung",
      reflection: "Reflexion",
      coaching: "Coaching",
      track: "Erfassen",
      decide: "Analysieren",
      tracked: "Erfasst",
      created: "Erstellt",
      basedOn: "Basierend auf",
      contextUsed: "Verwendeter Kontext",
      references: "Referenzen",
      values: "Werte",
      goals: "Ziele",
      patterns: "Muster",
      usingContext: "Verwende",
      rememberThis: "Merken",
      saved: "Gespeichert",
      remember: "Merken",
      goDeeper: "Vertiefen",
      continueWith: "Lass uns erkunden...",
      balanced: "Ausgewogen",
      modeSupport: "Unterstützung",
      modeCelebration: "Feier",
      modeExploration: "Erkundung",
      lifeIntelligence: "Life Intelligence",
      source: "Quelle",
      sources: "Quellen",
      memoryWhisper: "Erinnerungen",
      moments: "Momente",
      people: "Personen",
      places: "Orte",
      decisions: "Entscheidungen",
      usedMemories: "Erinnerungen",
      insight: "Erkenntnis",
      iSee: "Ich sehe dass",
      iNotice: "Ich bemerke dass",
      microAction: "Probiere das",
      sintonia: "Ausrichtung",
      sintoniaHigh: "Im Einklang",
      sintoniaMedium: "Anpassung nötig",
      sintoniaLow: "Achtung",
      basedOnYourHistory: "Aus deiner Geschichte",
      fromYourPatterns: "Aus deinen Mustern",
      connectedTo: "Verbunden mit",
      saveValue: "Wert speichern",
      saveGoal: "Ziel speichern"
    },
    voice: {
      title: "Sprachnotiz",
      tapToRecord: "Tippe auf das Mikrofon zum Aufnehmen",
      recording: "Aufnahme läuft...",
      transcribing: "Transkribiere...",
      saveNote: "Notiz speichern",
      cancel: "Abbrechen",
      micPermission: "Mikrofonberechtigung erforderlich",
      noSpeech: "Keine Sprache erkannt",
      error: "Fehler bei der Aufnahme"
    },
    decisions: {
      subtitle: "Löse deine Zweifel mit Daten",
      analyzeChoice: "Wahl analysieren",
      analyzing: "Analysiere...",
      discussInChat: "Im Chat besprechen",
      resolve: "Lösen",
      resolved: "Gelöst",
      pending: "Ausstehend",
      options: "Optionen",
      pros: "Vorteile",
      cons: "Nachteile",
      bestOption: "Empfohlene Option",
      reasoning: "Begründung",
      nextStep: "Nächster Schritt",
      alignment: "Ausrichtung"
    },
    home: {
      goodMorning: "Guten Morgen",
      goodAfternoon: "Guten Tag",
      goodEvening: "Guten Abend",
      todayIs: "Heute ist",
      recentMoments: "Aktuelle Momente",
      noMoments: "Noch keine Momente",
      startTracking: "Beginne deine Momente zu erfassen",
      addMoment: "Moment hinzufügen",
      placeholder: "Was passiert gerade?",
      saving: "Speichere...",
      save: "Speichern",
      readyToListen: "Bereit zuzuhören.",
      noMicFound: "Kein Mikrofon gefunden",
      noTextDetected: "Kein Text erkannt",
      writeOrHoldMic: "Schreibe oder halte das Mikrofon gedrückt",
      transcribing: "Transkribiere...",
      oneMoment: "Einen Moment",
      recordingInProgress: "Aufnahme läuft",
      viewAll: "Alles anzeigen",
      startCapturing: "Beginne deine Momente zu erfassen",
      iKnowYouAt: "Ich kenne dich zu",
      micPermissionError: "Erlaube Mikrofonzugriff in Einstellungen",
      recordingError: "Fehler beim Starten der Aufnahme",
      recordingTooShort: "Aufnahme zu kurz",
      transcriptionError: "Transkriptionsfehler. Bitte erneut versuchen.",
      greeting: "Hallo",
      recent: "Aktuell",
      defaultUser: "Benutzer",
      connection: "Verbindung",
      connections: "Verbindungen",
      timeNow: "Jetzt",
      timeMinutesAgo: "m her",
      timeHoursAgo: "h her",
      timeYesterday: "Gestern",
      timeDaysAgo: "T her"
    },
    journal: {
      title: "Deine Geschichte",
      subtitle: "Alle deine Momente",
      noEntries: "Keine Tagebucheinträge",
      startJourney: "Beginne deine Reise",
      searchPlaceholder: "Suche in deinen Momenten...",
      allCategories: "Alle Kategorien",
      viewDetails: "Details anzeigen",
      aiInsight: "KI-Einblick",
      entries: "Momente",
      vitalImpact: "Vitale Auswirkung",
      connections: "Verbindungen",
      delete: "Löschen",
      deleteConfirm: "Diesen Moment löschen?",
      noMemoriesYet: "Noch keine Erinnerungen",
      noResults: "Keine Ergebnisse gefunden",
      empty: "Noch keine Momente",
      people: "Personen",
      places: "Orte"
    },
    askSubtitle: "Existenzieller Dialog",
    askLabels: {
      moments: "Momente",
      decisions: "Entscheidungen",
      patterns: "Muster"
    },
    askSuggestions: {
      goalProgress: "Dein Weg zu \"{goal}\"",
      analyzeValues: "Was deine Werte offenbaren",
      difficultDecision: "Diese Entscheidung gemeinsam erkunden",
      whatCanYouDo: "Wie ich dich heute unterstützen kann",
      analyzeDay: "Die Bedeutung deines Tages",
      helpDecide: "Deine Prioritäten klären",
      whatWouldHelp: "Was dich jetzt befreien könnte",
      tellMeMore: "Lass uns das gemeinsam erkunden",
      whatBlocksYou: "Die verborgenen Widerstände",
      howToCelebrate: "Die Bedeutung dieses Meilensteins",
      whatWorked: "Was dich hierher gebracht hat",
      nextSteps: "Der Schritt, der wirklich zählt",
      deeperOnTopic: "Das Herz dieser Sache",
      howDoesItFeel: "Was dein Körper dir sagt",
      whatToChange: "Was du verwandeln möchtest",
      firstTime: {
        tellMeAboutToday: "Erzähle mir etwas, das heute passiert ist",
        whatAreYouThinking: "Teile mit, was du gerade denkst",
        aDecisionToMake: "Ich habe eine Entscheidung zu treffen"
      }
    },
    askPage: {
      momentSaved: "Moment gespeichert!",
      saveError: "Fehler beim Speichern",
      saved: "Gespeichert",
      writeOrAskAdvice: "Schreibe oder frage um Rat...",
      decisionCreated: "Entscheidung erstellt!",
      valueSaved: "Wert gespeichert!",
      goalSaved: "Ziel gespeichert!"
    },
    decisionsPage: {
      subtitle: "Löse deine Zweifel mit Daten",
      strategicAnalysis: "Strategische Analyse",
      choicePlaceholder: "Welche Wahl musst du treffen?",
      analyzeDilemma: "Dilemma analysieren",
      aiAdvice: "KI-Rat",
      suggested: "Vorgeschlagen",
      nextStep: "Nächster Schritt",
      discussNextStep: "Nächsten Schritt besprechen",
      compareOptions: "Optionen vergleichen",
      discussInChat: "Im Chat besprechen",
      decided: "Entschieden",
      reopen: "Wieder öffnen",
      delete: "Löschen",
      waitingForChoice: "Warten auf einen Zweifel, um ihn in eine Wahl umzuwandeln.",
      talkInChat: "Im Chat sprechen",
      analyzed: "Analysiert",
      inAnalysis: "Wird analysiert"
    },
    insightsPage: {
      knowYouAt: "Ich kenne dich zu",
      knowDeep: "Ich kenne dich tiefgründig!",
      niceConnection: "Wir haben eine gute Verbindung aufgebaut.",
      learningAboutYou: "Ich lerne viel über dich.",
      continueLearning: "Lass uns weiter kennenlernen!",
      beginningJourney: "Wir stehen erst am Anfang der Reise.",
      moments: "Momente",
      patterns: "Muster",
      decisions: "Entscheidungen",
      people: "Menschen",
      whatIKnow: "Was ich über dich weiß",
      yourValues: "Deine Werte",
      yourGoals: "Deine Ziele",
      addYourValues: "+ Deine Werte hinzufügen",
      addYourGoals: "+ Deine Ziele hinzufügen",
      newValue: "Neuer Wert...",
      newGoal: "Neues Ziel...",
      toKnowYouBetter: "Um dich besser kennenzulernen",
      logout: "Abmelden",
      logoutConfirm: "Möchtest du dich abmelden?",
      logoutDescription: "Du wirst von der App abgemeldet. Du kannst dich jederzeit wieder anmelden."
    },
    insights: {
      memory: "Erinnerung",
      flow: "Flow",
      personality: "Persönlichkeit",
      settings: "Einstellungen",
      yourValues: "Deine Werte",
      yourGoals: "Deine Ziele",
      addValue: "Wert hinzufügen",
      addGoal: "Ziel hinzufügen",
      save: "Speichern",
      cancel: "Abbrechen",
      edit: "Bearbeiten",
      peopleInYourLife: "Menschen in deinem Leben",
      patternsDetected: "Erkannte Muster",
      refreshPatterns: "Muster aktualisieren",
      userName: "Benutzername",
      dangerZone: "Gefahrenzone",
      deleteAllData: "Alle Daten löschen",
      deleteWarning: "Diese Aktion ist unwiderruflich",
      confirmDelete: "Löschung bestätigen",
      tabMemory: "Erinnerung",
      tabTone: "Ton",
      tabConfig: "Config",
      lifeAreas: "Lebensbereiche",
      yourEmotions: "Deine Emotionen",
      patternsIdentified: "Erkannte Muster",
      importantPeople: "Wichtige Menschen",
      timesObserved: "mal beobachtet",
      timesMentioned: "erwähnt",
      namePlaceholder: "Name...",
      coreAlignmentExcellent: "ausgezeichnet",
      coreAlignmentEvolving: "in Entwicklung",
      coreAlignmentMessage: "Deine Core-Ausrichtung ist",
      addPerson: "Person hinzufügen",
      addPersonPlaceholder: "Name der Person...",
      relationshipPlaceholder: "Beziehung (z.B. Freund, Kollege)...",
      restoreHiddenPeople: "Versteckte Personen wiederherstellen",
      hiddenPeopleCount: "versteckte Personen",
      manuallyAdded: "manuell hinzugefügt",
      yourTrends: "Deine Trends",
      fiveDimensions: "Deine 5 Dimensionen",
      emotionFlowTitle: "Emotionsfluss",
      emotionFlowSubtitle: "Wie du dich gefühlt hast",
      emotionFlowDaysTracked: "Tage erfasst",
      lastDays: "Letzte {n} Tage",
      rhythmTitle: "Dein Rhythmus",
      rhythmSubtitle: "Wann du dich am besten fühlst",
      rhythmBestMoment: "Beste Zeit",
      levelLow: "Niedrig",
      levelMedium: "Mittel",
      levelHigh: "Hoch",
      goalAlignmentTitle: "Ziel-Ausrichtung",
      goalAlignmentSubtitle: "Wie gut du deine Ziele verfolgst",
      alignment: "Ausrichtung",
      addGoals: "Füge deine Ziele hinzu",
      showMoreGoals: "Mehr anzeigen",
      showLessGoals: "Weniger anzeigen",
      selectGoals: "Ziele auswählen",
      selectedCount: "ausgewählt",
      noData: "Keine Daten",
      trendsIntroTitle: "Entdecke Deine Lebensmuster",
      trendsIntroDescription: "Die KI analysiert kontinuierlich deine Momente, um bedeutungsvolle Trends zu identifizieren. Diese Erkenntnisse helfen dir zu verstehen, was dich glücklich macht, wann du am produktivsten bist und wie sich deine Emotionen im Laufe der Zeit entwickeln.",
      basedOn: "Basierend auf",
      momentsTracked: "erfasste Momente",
      connectedTo: "Verbunden mit",
      patternInsight: "Erkenntnis",
      noPatterns: "Teile weiter Momente, um deine Muster zu entdecken",
      patternTypeEmotional: "Emotional",
      patternTypeBehavioral: "Verhaltensmäßig",
      patternTypeTemporal: "Zeitlich",
      patternTypeRelational: "Relational",
      patternTypeDecision: "Entscheidung"
    },
    journalLinks: {
      similar: "Ähnlich",
      cause: "Ursache",
      effect: "Wirkung",
      contradiction: "Widerspruch",
      progression: "Entwicklung",
      relatedDecision: "Entscheidung"
    },
    common: {
      loading: "Laden...",
      error: "Fehler",
      retry: "Erneut versuchen",
      close: "Schließen",
      confirm: "Bestätigen",
      back: "Zurück",
      next: "Weiter",
      finish: "Fertig",
      skip: "Überspringen",
      or: "oder",
      and: "und"
    },
    errors: {
      generic: "Etwas ist schief gelaufen",
      network: "Verbindungsfehler",
      aiUnavailable: "KI vorübergehend nicht verfügbar",
      saveFailed: "Speichern fehlgeschlagen",
      loadFailed: "Laden fehlgeschlagen"
    },
    limits: {
      title: "Du hast dein monatliches Limit erreicht",
      subtitle: "Du nutzt Egovex auf wunderbare Weise. Um weiter zu wachsen, erwäge ein Upgrade.",
      momentsLimit: "Momente",
      patternsLimit: "Muster",
      decisionsLimit: "Entscheidungen",
      chatMessagesLimit: "Nachrichten",
      currentUsage: "Verwendet",
      monthlyLimit: "Monatliches Limit",
      resetsOn: "Wird zurückgesetzt am",
      upgradeTitle: "Entfalte dein Potenzial",
      upgradeDescription: "Mit Premium hast du unbegrenzten Zugang zu allen Tools für dein persönliches Wachstum.",
      upgradeButton: "Auf Premium upgraden",
      maybeLater: "Jetzt nicht, danke",
      freeFeatures: "Kostenloser Plan",
      premiumFeatures: "Premium Plan"
    }
  },
  French: {
    navHome: "Pulse",
    navDiary: "Histoire",
    navAsk: "Intellect",
    navDecisions: "Chemin",
    navAnalysis: "Synthèse",
    navProfile: "Profil",
    navSettings: "Système",
    mapYourPath: "Vos trajectoires",
    noDecisions: "Paix totale.",
    newDilemma: "Explorer le doute",
    decisionPlaceholder: "Qu'avez-vous à l'esprit ?",
    mapDecisionAction: "Tracer le chemin",
    pathsMapped: "choix actifs",
    visualMoment: "Instance Visuelle",
    diagnosticsSub: "Résonance actuelle",
    vitalBalance: "Équilibre Vital",
    trajectory: "Tendance",
    personality: "Ton IA",
    warmth: "Empathie",
    directness: "Clarté",
    depth: "Profondeur",
    tuning: "Calibration",
    testVoice: "Tester le ton",
    calibrating: "Syntonisation...",
    voicePreview: "Aperçu du ton",
    selectArchetype: "Sélectionner la fréquence",
    personalityNotice: "Ces paramètres définissent la manière dont l'IA perçoit et répond à votre réalité.",
    mentor: "Mentor",
    mentorDesc: "Axé sur l'évolution.",
    companion: "Partenaire",
    companionDesc: "Présent et soutenant.",
    stoic: "Logique",
    stoicDesc: "Analytique et net.",
    philosopher: "Essence",
    philosopherDesc: "Profond et poétique.",
    interfaceLang: "Langue",
    resetSystem: "Réinitialiser Egovex",
    syncing: "Connexion...",
    loadingProfile: "Chargement du profil...",
    loadingSync: "Synchronisation de vos données",
    avgAlignment: "Flux",
    alignmentScore: "Alignement Core",
    intentionality: "Intentionnalité",
    performanceHigh: "Pic",
    performanceMid: "En croissance",
    performanceLow: "À l'écoute",
    dialogue: "Dialogue",
    writeFreely: "Réfléchir...",
    listening: "Je vous écoute...",
    saveThought: "Intégrer",
    syncComplete: "Lien Actif",
    loadingMoments: "Chargement des moments...",
    voiceInstruction: "Vous êtes une essence de sagesse. Écoutez et répondez avec une empathie viscérale mais concise.",
    categories: {
      Health: "Corps",
      Focus: "Esprit",
      Connection: "Cœur",
      Spirit: "Âme",
      Finance: "Matière"
    },
    chatWelcome: {
      greeting: "c'est votre espace.",
      greetingSubtitle: "Bonjour,",
      description: "Écrivez ce que vous avez en tête. Une réflexion, un doute, une décision à prendre. Je suis là pour écouter et réfléchir avec vous.",
      startHere: "Commencez ici"
    },
    chatCard: {
      support: "Soutien",
      celebration: "Célébration",
      exploration: "Exploration",
      reflection: "Réflexion",
      coaching: "Coaching",
      track: "Enregistrer",
      decide: "Analyser",
      tracked: "Enregistré",
      created: "Créé",
      basedOn: "Basé sur",
      contextUsed: "Contexte utilisé",
      references: "Références",
      values: "Valeurs",
      goals: "Objectifs",
      patterns: "Schémas",
      usingContext: "Utilise",
      rememberThis: "Se souvenir",
      saved: "Enregistré",
      remember: "Se souvenir",
      goDeeper: "Approfondir",
      continueWith: "Explorons ensemble...",
      balanced: "Équilibré",
      modeSupport: "Soutien",
      modeCelebration: "Célébration",
      modeExploration: "Exploration",
      lifeIntelligence: "Life Intelligence",
      source: "source",
      sources: "sources",
      memoryWhisper: "souvenirs",
      moments: "moments",
      people: "personnes",
      places: "lieux",
      decisions: "décisions",
      usedMemories: "souvenirs",
      insight: "Observation",
      iSee: "Je vois que",
      iNotice: "Je remarque que",
      microAction: "Essaie ça",
      sintonia: "Alignement",
      sintoniaHigh: "En harmonie",
      sintoniaMedium: "À ajuster",
      sintoniaLow: "Attention",
      basedOnYourHistory: "De ton histoire",
      fromYourPatterns: "De tes schémas",
      connectedTo: "Connecté à",
      saveValue: "Sauvegarder valeur",
      saveGoal: "Sauvegarder objectif"
    },
    voice: {
      title: "Note Vocale",
      tapToRecord: "Appuyez sur le micro pour enregistrer",
      recording: "Enregistrement...",
      transcribing: "Transcription...",
      saveNote: "Enregistrer la note",
      cancel: "Annuler",
      micPermission: "Autorisation du micro requise",
      noSpeech: "Aucune voix détectée",
      error: "Erreur lors de l'enregistrement"
    },
    decisions: {
      subtitle: "Résolvez vos doutes avec des données",
      analyzeChoice: "Analyser le choix",
      analyzing: "Analyse...",
      discussInChat: "Discuter dans le chat",
      resolve: "Résoudre",
      resolved: "Résolue",
      pending: "En attente",
      options: "Options",
      pros: "Avantages",
      cons: "Inconvénients",
      bestOption: "Option recommandée",
      reasoning: "Raisonnement",
      nextStep: "Prochaine étape",
      alignment: "Alignement"
    },
    home: {
      goodMorning: "Bonjour",
      goodAfternoon: "Bon après-midi",
      goodEvening: "Bonsoir",
      todayIs: "Aujourd'hui c'est",
      recentMoments: "Moments récents",
      noMoments: "Pas encore de moments",
      startTracking: "Commencez à enregistrer vos moments",
      addMoment: "Ajouter un moment",
      placeholder: "Que se passe-t-il ?",
      saving: "Enregistrement...",
      save: "Enregistrer",
      readyToListen: "Prêt à vous écouter.",
      noMicFound: "Aucun microphone trouvé",
      noTextDetected: "Aucun texte détecté",
      writeOrHoldMic: "Écrivez ou maintenez le micro",
      transcribing: "Transcription...",
      oneMoment: "Un instant",
      recordingInProgress: "Enregistrement en cours",
      viewAll: "Tout voir",
      startCapturing: "Commencez à capturer vos moments",
      iKnowYouAt: "Je vous connais à",
      micPermissionError: "Autorisez l'accès au micro dans les paramètres",
      recordingError: "Erreur de démarrage d'enregistrement",
      recordingTooShort: "Enregistrement trop court",
      transcriptionError: "Erreur de transcription. Veuillez réessayer.",
      greeting: "Bonjour",
      recent: "Récents",
      defaultUser: "Utilisateur",
      connection: "connexion",
      connections: "connexions",
      timeNow: "Maintenant",
      timeMinutesAgo: "m passé",
      timeHoursAgo: "h passé",
      timeYesterday: "Hier",
      timeDaysAgo: "j passé"
    },
    journal: {
      title: "Votre histoire",
      subtitle: "Tous vos moments",
      noEntries: "Aucune entrée de journal",
      startJourney: "Commencez votre voyage",
      searchPlaceholder: "Rechercher dans vos moments...",
      allCategories: "Toutes les catégories",
      viewDetails: "Voir les détails",
      aiInsight: "Insight IA",
      entries: "Moments",
      vitalImpact: "Impact Vital",
      connections: "Connexions",
      delete: "Supprimer",
      deleteConfirm: "Supprimer ce moment ?",
      noMemoriesYet: "Pas encore de souvenirs",
      noResults: "Aucun résultat trouvé",
      empty: "Pas encore de moments",
      people: "Personnes",
      places: "Lieux"
    },
    askSubtitle: "Dialogue existentiel",
    askLabels: {
      moments: "moments",
      decisions: "décisions",
      patterns: "schémas"
    },
    askSuggestions: {
      goalProgress: "Ton chemin vers \"{goal}\"",
      analyzeValues: "Ce que révèlent tes valeurs",
      difficultDecision: "Explorons cette décision ensemble",
      whatCanYouDo: "Comment je peux te soutenir aujourd'hui",
      analyzeDay: "Le sens de ta journée",
      helpDecide: "Clarifier tes priorités",
      whatWouldHelp: "Ce qui pourrait te débloquer maintenant",
      tellMeMore: "Explorons cela ensemble",
      whatBlocksYou: "Les résistances cachées",
      howToCelebrate: "La signification de cette étape",
      whatWorked: "Ce qui t'a amené ici",
      nextSteps: "Le pas qui compte vraiment",
      deeperOnTopic: "Le cœur de cette question",
      howDoesItFeel: "Ce que ton corps te dit",
      whatToChange: "Ce que tu veux transformer",
      firstTime: {
        tellMeAboutToday: "Raconte-moi quelque chose qui s'est passé aujourd'hui",
        whatAreYouThinking: "Partage ce que tu penses en ce moment",
        aDecisionToMake: "J'ai une décision à prendre"
      }
    },
    askPage: {
      momentSaved: "Moment enregistré !",
      saveError: "Erreur d'enregistrement",
      saved: "Enregistré",
      writeOrAskAdvice: "Écrivez ou demandez conseil...",
      decisionCreated: "Décision créée !",
      valueSaved: "Valeur enregistrée !",
      goalSaved: "Objectif enregistré !"
    },
    decisionsPage: {
      subtitle: "Résolvez vos doutes avec des données",
      strategicAnalysis: "Analyse Stratégique",
      choicePlaceholder: "Quel choix devez-vous faire ?",
      analyzeDilemma: "Analyser le dilemme",
      aiAdvice: "Conseil IA",
      suggested: "Suggéré",
      nextStep: "Prochaine étape",
      discussNextStep: "Discuter de la prochaine étape",
      compareOptions: "Comparer les options",
      discussInChat: "Discuter dans le chat",
      decided: "Décidé",
      reopen: "Rouvrir",
      delete: "Supprimer",
      waitingForChoice: "En attente d'un doute à transformer en choix.",
      talkInChat: "Parler dans le chat",
      analyzed: "Analysée",
      inAnalysis: "En analyse"
    },
    insightsPage: {
      knowYouAt: "Je vous connais à",
      knowDeep: "Je vous connais profondément !",
      niceConnection: "Nous avons construit une belle connexion.",
      learningAboutYou: "J'apprends beaucoup sur vous.",
      continueLearning: "Continuons à nous connaître !",
      beginningJourney: "Nous ne faisons que commencer le voyage.",
      moments: "Moments",
      patterns: "Schémas",
      decisions: "Décisions",
      people: "Personnes",
      whatIKnow: "Ce que je sais de vous",
      yourValues: "Vos valeurs",
      yourGoals: "Vos objectifs",
      addYourValues: "+ Ajouter vos valeurs",
      addYourGoals: "+ Ajouter vos objectifs",
      newValue: "Nouvelle valeur...",
      newGoal: "Nouvel objectif...",
      toKnowYouBetter: "Pour mieux vous connaître",
      logout: "Déconnexion",
      logoutConfirm: "Voulez-vous vous déconnecter ?",
      logoutDescription: "Vous serez déconnecté de l'application. Vous pouvez vous reconnecter quand vous le souhaitez."
    },
    insights: {
      memory: "Mémoire",
      flow: "Flux",
      personality: "Personnalité",
      settings: "Paramètres",
      yourValues: "Vos valeurs",
      yourGoals: "Vos objectifs",
      addValue: "Ajouter une valeur",
      addGoal: "Ajouter un objectif",
      save: "Enregistrer",
      cancel: "Annuler",
      edit: "Modifier",
      peopleInYourLife: "Personnes dans votre vie",
      patternsDetected: "Schémas détectés",
      refreshPatterns: "Actualiser les schémas",
      userName: "Nom d'utilisateur",
      dangerZone: "Zone de danger",
      deleteAllData: "Supprimer toutes les données",
      deleteWarning: "Cette action est irréversible",
      confirmDelete: "Confirmer la suppression",
      tabMemory: "Mémoire",
      tabTone: "Ton",
      tabConfig: "Config",
      lifeAreas: "Domaines de vie",
      yourEmotions: "Vos émotions",
      patternsIdentified: "Schémas identifiés",
      importantPeople: "Personnes importantes",
      timesObserved: "fois observé",
      timesMentioned: "mentionné",
      namePlaceholder: "Nom...",
      coreAlignmentExcellent: "excellent",
      coreAlignmentEvolving: "en évolution",
      coreAlignmentMessage: "Votre alignement core est",
      addPerson: "Ajouter une personne",
      addPersonPlaceholder: "Nom de la personne...",
      relationshipPlaceholder: "Relation (ex. ami, collègue)...",
      restoreHiddenPeople: "Restaurer les personnes masquées",
      hiddenPeopleCount: "personnes masquées",
      manuallyAdded: "ajoutée manuellement",
      yourTrends: "Vos Tendances",
      fiveDimensions: "Vos 5 Dimensions",
      emotionFlowTitle: "Flux Émotionnel",
      emotionFlowSubtitle: "Comment vous vous êtes senti",
      emotionFlowDaysTracked: "jours suivis",
      lastDays: "Derniers {n} jours",
      rhythmTitle: "Votre Rythme",
      rhythmSubtitle: "Quand vous vous sentez le mieux",
      rhythmBestMoment: "Meilleur moment",
      levelLow: "Bas",
      levelMedium: "Moyen",
      levelHigh: "Haut",
      goalAlignmentTitle: "Alignement des Objectifs",
      goalAlignmentSubtitle: "À quel point vous suivez vos objectifs",
      alignment: "Alignement",
      addGoals: "Ajoutez vos objectifs",
      showMoreGoals: "Afficher plus",
      showLessGoals: "Afficher moins",
      selectGoals: "Sélectionner les objectifs",
      selectedCount: "sélectionnés",
      noData: "Pas de données",
      trendsIntroTitle: "Découvrez Vos Schémas de Vie",
      trendsIntroDescription: "L'IA analyse en permanence vos moments pour identifier des tendances significatives. Ces aperçus vous aident à comprendre ce qui vous fait vous sentir bien, quand vous êtes le plus productif et comment vos émotions évoluent au fil du temps.",
      basedOn: "Basé sur",
      momentsTracked: "moments suivis",
      connectedTo: "Connecté à",
      patternInsight: "Aperçu",
      noPatterns: "Continuez à partager des moments pour découvrir vos schémas",
      patternTypeEmotional: "Émotionnel",
      patternTypeBehavioral: "Comportemental",
      patternTypeTemporal: "Temporel",
      patternTypeRelational: "Relationnel",
      patternTypeDecision: "Décision"
    },
    journalLinks: {
      similar: "Similaire",
      cause: "Cause",
      effect: "Effet",
      contradiction: "Contradiction",
      progression: "Progression",
      relatedDecision: "Décision"
    },
    common: {
      loading: "Chargement...",
      error: "Erreur",
      retry: "Réessayer",
      close: "Fermer",
      confirm: "Confirmer",
      back: "Retour",
      next: "Continuer",
      finish: "Terminer",
      skip: "Passer",
      or: "ou",
      and: "et"
    },
    errors: {
      generic: "Quelque chose s'est mal passé",
      network: "Erreur de connexion",
      aiUnavailable: "IA temporairement indisponible",
      saveFailed: "Échec de l'enregistrement",
      loadFailed: "Échec du chargement"
    },
    limits: {
      title: "Vous avez atteint votre limite mensuelle",
      subtitle: "Vous utilisez Egovex de manière formidable. Pour continuer à grandir, envisagez la mise à niveau.",
      momentsLimit: "moments",
      patternsLimit: "schémas",
      decisionsLimit: "décisions",
      chatMessagesLimit: "messages",
      currentUsage: "Utilisés",
      monthlyLimit: "Limite mensuelle",
      resetsOn: "Se réinitialise le",
      upgradeTitle: "Libérez votre potentiel",
      upgradeDescription: "Avec Premium, accédez sans limite à tous les outils pour votre croissance personnelle.",
      upgradeButton: "Passer à Premium",
      maybeLater: "Pas maintenant, merci",
      freeFeatures: "Plan gratuit",
      premiumFeatures: "Plan Premium"
    }
  }
};
